// Plan pricing in GBP. Single source of truth for the landing page pricing
// cards and the sign-up RegistrationStepper so the two never drift. The
// amounts actually charged come from the Stripe prices in
// onboardingHandler.js - keep both in step when pricing changes.
//
// `annual` is the yearly total: ten months at the monthly price, because
// annual billing is sold as two months free. The headline per-month figure
// follows the billing toggle: `monthly`, or what a year works out at per
// month (annual / 12) once the two free months are in.
// Plans are not tied to a location count: any business can pick any plan.
// The self-serve plans are billed once per location, which is the only
// reason sign-up asks how many there are. Established is priced on request,
// so it carries no amounts and cannot be bought through self-serve checkout.

export const PLAN_PRICING = {
  starter: {
    monthly: 18,
    annual: 180,
    perLocation: true,
  },
  scaler: {
    monthly: 30,
    annual: 300,
    perLocation: true,
  },
  established: {
    contactOnly: true,
  },
};

// Plans that can be bought through checkout, in display order.
export const SELF_SERVE_PLANS = Object.keys(PLAN_PRICING).filter(
  (key) => !PLAN_PRICING[key].contactOnly
);

export const isSelfServePlan = (key) => SELF_SERVE_PLANS.includes(key);

// Checkout bills per location and the backend accepts a quantity of at most
// this many (createStripeCheckoutSession in lulu-backend). Sign-up offers a
// "20+" option for anything larger, which goes through the Established
// enquiry instead of checkout.
export const MAX_SELF_SERVE_LOCATIONS = 20;

// Shown on the plan cards wherever they're listed. Checkout can bill up to
// MAX_SELF_SERVE_LOCATIONS, so the self-serve plans cover that many and
// Established takes over beyond it.
export const SELF_SERVE_LOCATIONS_NOTE = `For up to ${MAX_SELF_SERVE_LOCATIONS} locations`;
export const ESTABLISHED_LOCATIONS_NOTE = `For businesses with more than ${MAX_SELF_SERVE_LOCATIONS} locations`;

// Prefilled enquiry for the Established "Contact us" CTAs.
export const ESTABLISHED_ENQUIRY_MESSAGE =
  "Hi Lulu team, I'm interested in the Established plan for my business. Please get in touch to discuss pricing and getting set up.";

// Enquiry sent from the sign-up flow, where the business name and location
// count are already known. The count is there to help the Lulu team size the
// conversation, not to qualify the business.
export const buildEstablishedEnquiry = ({ businessName, locations }) => {
  const name = (businessName || "").trim();
  const who = name ? `I'm signing up ${name}` : "I'm signing up my business";
  const count = Number(locations);
  let where = "";
  if (Number.isInteger(count) && count > MAX_SELF_SERVE_LOCATIONS) {
    where = ` and we have more than ${MAX_SELF_SERVE_LOCATIONS} locations`;
  } else if (Number.isInteger(count) && count >= 1) {
    where = ` and we have ${count} ${count === 1 ? "location" : "locations"}`;
  }
  return `Hi Lulu team, ${who}${where}. I'd like to talk about the Established plan. Please get in touch to discuss pricing and getting set up.`;
};

// Prefilled message for the dashboard billing card's "contact us" link. Plan
// changes go through the Lulu team rather than the Stripe portal, so the
// business ID is included to save the team a lookup.
export const buildPlanChangeMessage = ({ businessId } = {}) => {
  const id = String(businessId || "").trim();
  const which = id ? ` for my business (ID: ${id})` : "";
  return `Hi Lulu team, I'd like to make a change to my plan${which}. Please get in touch to talk through the options.`;
};

export const formatGbp = (amount) =>
  Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);

// Headline per-month figure for the chosen billing period: the monthly
// price, or what a year works out at per month with the two free months in.
export const monthlyEquivalent = (planKey, billingPeriod) => {
  const plan = PLAN_PRICING[planKey];
  return formatGbp(billingPeriod === "annual" ? plan.annual / 12 : plan.monthly);
};
